<template>
  <CommonNoticeCard
    :class="[b()]"
  >
    <div
      slot="title"
      :class="b('title')"
    >
      <em class="zkicon iconicon_shipin" />
      <template v-if="loading">
        <span>{{ $t('notice.recorder.uploading') }}</span>
      </template>
      <template v-else-if="!linkUrl">
        {{ $t('notice.recorder.uploadFailed') }}
      </template>
      <template v-else>
        {{ $t('notice.recorder.uploadSuccess') }}
      </template>
    </div>
    <template #content>
      <span
        v-if="!editing || loading"
        :class="b('name')"
      >
        {{ videoName }}
      </span>
      <el-tooltip
        v-if="editing"
        effect="dark"
        :content="$t('project.inputTooltip')"
        placement="top"
      >
        <el-input
          ref="input"
          v-model="updateName"
          v-blur-to-enter
          v-trim="updateName"
          :readonly="updateLoading.loading"
          size="small"
          type="textarea"
          :autosize="{minRows: 1, maxRows: 2}"
          show-word-limit
          :maxlength="50"
          @blur="editing = false"
          @keypress.native.enter.exact.prevent="updateLoading.fn"
        />
      </el-tooltip>
      <em
        v-if="!loading && !editing"
        :class="['zkicon iconedit', b('edit-icon')]"
        @click="editMode"
      />
      <el-progress
        v-if="loading"
        :percentage="percent"
        :show-text="false"
        :stroke-width="10"
      />
    </template>
    <template
      v-if="!loading"
      #actions
    >
      <template v-if="linkUrl">
        <el-button
          v-clipboard
          type="plaintext2"
          icon="zkicon iconlianjie"
          :data-clipboard-text="linkUrl"
          @clipboard-success="onCopySuccess"
          @click.stop
        >
          {{ $t('notice.recorder.copyLink') }}
        </el-button>
        <el-button
          v-if="from !== 'issueDesc'"
          type="plaintext2"
          icon="zkicon iconxiala_xinjian"
          :class="b('create-btn')"
          @click.stop="createIssue"
        >
          {{ $t('navbar.createNewIssue') }}
        </el-button>
      </template>
      <template v-else>
        <el-button
          type="plaintext2"
          icon="zkicon iconxiazai_xiazai"
          :loading="downloadLoading"
          @click.stop="download"
        >
          {{ $t('notice.recorder.download') }}
        </el-button>
        <el-button
          type="plaintext2"
          icon="zkicon iconzhongzuo"
          @click.stop="retry"
        >
          {{ $t('notice.recorder.reUpload') }}
        </el-button>
      </template>
    </template>
  </CommonNoticeCard>
</template>

<script>

import CommonNoticeCard from '@/components/notice/CommonNoticeCard';
import theme from '@zk/theme/lib/variables.scss';
import { updateRecorder } from '@/api/recorder';
import { createLoadingEvent, tryToNumber } from '@zk/framework-vue';
import { useIssueDetail } from 'liga-detail/src/components/IssueDrawer/Store';
import { relationRecordToIssue } from '@/components/relationRecorderToIssue';

export default {
  name: 'UploadRecorderNoticeCard',
  components: { CommonNoticeCard },
  bemNS: theme.bemNS,
  props: {
    recordUpload: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      percent: 0,
      linkUrl: '',
      videoUrl: '',
      videoCode: '',
      videoAuthor: '',
      fileId: null,
      editing: false,
      videoName: '',
      videoSuffix: '',
      updateName: null,
      downloadLoading: false,
      isRetry: false,
      updateLoading: createLoadingEvent(this.onChangeName)
    };
  },
  mounted() {
    this.init();
    this.$parent.$on('closed', this.onClosed);
  },
  beforeDestroy() {
    this.recordUpload.off('uploadProgress', this.onProgressChange);
    this.recordUpload.off('uploadStatusChange', this.onUploadStatusChange);
  },
  methods: {
    async init() {
      if (!this.recordUpload){
        return;
      }
      const { fileName, text, from, fileSuffix } = this.recordUpload.recorderResult;

      this.videoName = fileName;
      this.audioText = text;
      this.from = from;
      this.videoSuffix = fileSuffix;

      this.recordUpload.on('uploadProgress', this.onProgressChange);
      this.recordUpload.on('uploadStatusChange', this.onUploadStatusChange);
    },
    retry() {
      this.isRetry = true;
      this.loading = true;
      this.recordUpload.retry();
    },
    onUploadStatusChange({ status, data }) {
      if (status === 'success') {
        const { videoUrl, videoCode, fileId, issueId } = data;

        this.fileId = fileId;
        this.videoUrl = videoUrl;
        this.videoCode = videoCode;
        this.videoAuthor = JSON.stringify({
          avatar: this.$store.getters['user/avatar'],
          name: this.$store.getters['user/userName']
        });
        this.linkUrl = this.$config.getResourceUrl('VUE_APP_PMS_URL', `/work/view-recorder?code=${fileId}`);
        this.loading = false;
        const issueDetailStore = useIssueDetail();
        if (issueId && issueDetailStore?.id !== issueId) {
          relationRecordToIssue(issueId, videoUrl, videoCode, this.videoName);
        } else {
          this.eventBus.$emit('recorder:uploaded', videoUrl, videoCode, this.videoName, this.videoAuthor, this.audioText, this.from);
        }
        this.sendNotice();
        this.$track.send('Video_UploadCard', { Upload_Result: '成功', Upload_Retry: this.isRetry ? '是' : '否' }, true);
      } else if (status === 'fail') {
        this.$track.send('Video_UploadCard', { Upload_Result: '失败', Upload_Retry: this.isRetry ? '是' : '否' }, true);
        this.loading = false;
      } else if (status === 'uploading') {
        this.loading = true;
      }
    },
    onProgressChange(progress) {
      this.percent = progress * 100 | 0;
    },
    sendNotice() {
      if (typeof window.Notification === 'undefined' || Notification.permission !== 'granted') {
        return;
      }
      const notice = new Notification(this.$t('notice.recorder.uploaded'));
      notice.onclick = () => {
        notice.close();
        window.focus();
      };
    },
    getVideoDuration() {
      const url = URL.createObjectURL(new Blob(this.video, { type: this.video[0].type }));
      const video = document.createElement('video');
      const defaultValue = tryToNumber(this.duration) || 0;
      return new Promise((resolve) => {
        // eslint-disable-next-line
        video.addEventListener('loadedmetadata', () => {
          const r = Math.floor(video.duration * 1000);
          if (r === Infinity || r === -Infinity) {
            return resolve(defaultValue);
          }
          return resolve(r);
        });
        video.addEventListener('error', () => resolve(defaultValue));
        video.src = url;
      }).finally(() => {
        URL.revokeObjectURL(url);
        video.remove();
      });
    },
    onCopySuccess() {
      this.$message.success(this.$t('message.copySuccess'));
      this.closeType = '复制链接';
      this.$parent.close();
      this.$track.send('Copy_VideoLink', { Copy_Path: '视频上传卡片' }, true);
    },
    async createIssue() {
      const isStorageAllow = await this.$store.dispatch('user/checkStorageRight');
      if (!isStorageAllow) {
        return;
      }
      this.eventBus.$emit('recorder:create', {
        videoUrl: this.videoUrl,
        videoCode: this.videoCode,
        videoName: this.videoName,
        videoAuthor: this.videoAuthor,
        audioText: this.audioText
      });
      this.closeType = '创建工作';
      this.$parent.close();
      this.$track.send('OnVideo_CreateIssue', { Create_Path: '视频上传卡片' }, true);
    },
    async onChangeName() {
      const name = this.updateName?.trim();
      if (!name || name === this.videoName) {
        this.editing = false;
        return;
      }
      await updateRecorder(this.fileId, { videoName: name });
      this.videoName = name;
      this.$track.send('Edit_VideoName', { Edit_Path: '视频上传卡片' }, true);
      this.updateName = null;
      this.editing = false;
    },
    onClosed() {
      this.cancelSource?.cancel();
      this.$track.send('Close_UploadCard', { Close_Moth: this.closeType || '右上角' }, true);
      this.closeType = null;
    },
    editMode() {
      this.editing = true;
      this.updateName = this.videoName;
      this.$nextTick(() => this.$refs.input?.focus());
    },
    download() {
      this.downloadLoading = true;
      try {
        const { mediaData } = this.recordUpload.recorderResult;
        const url = URL.createObjectURL(mediaData);
        const link = document.createElement('a');
        document.body.appendChild(link);
        link.style.display = 'none';
        link.href = url;
        link.download = `${this.videoName}.${this.videoSuffix}`;
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } finally {
        setTimeout(() => {
          this.downloadLoading = false;
        }, 1000);
      }
    }
  }
};
</script>

<style lang="scss">
@include b(upload-recorder-notice) {
  .el-notification__content {
    margin-top: 0;
  }
}
@include b(upload-recorder-notice-card) {
  .el-textarea__inner {
    padding-right: 50px;
    word-break: break-all;
  }
  @include e((title, name)) {
    line-height: 21px;
    height: 21px;
    color: var(--font-color-1);
  }
  @include e(title) {
    margin-bottom: var(--m-small);
    display: flex;
    align-items: center;
  }
  .iconicon_shipin {
    margin-right: var(--m-mini);
    color: #F0597C;
    font-size: 18px;
  }
  .el-progress {
    margin-top: var(--m-small);
  }
  @include e(edit-icon) {
    font-size: 18px;
    text-decoration: none;
    vertical-align: bottom;
    cursor: pointer;
    &:hover {
      color: var(--primary-color);
    }
  }
}
</style>
